import React from 'react';
import { ExternalLink } from 'lucide-react';
import { RatingWidget } from './RatingWidget';

const safeHref = (url) => (/^https?:\/\//i.test((url || '').trim()) ? url : '#');
const URL_RE = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g;

export const ACCENTS = {
  indigo:  { dot: 'bg-indigo-400',  title: 'text-indigo-300',  border: 'border-indigo-500/40',  bg: 'bg-indigo-900/20',  link: 'text-indigo-300' },
  emerald: { dot: 'bg-emerald-400', title: 'text-emerald-300', border: 'border-emerald-500/40', bg: 'bg-emerald-900/20', link: 'text-emerald-300' },
  cyan:    { dot: 'bg-cyan-400',    title: 'text-cyan-300',    border: 'border-cyan-500/40',    bg: 'bg-cyan-900/20',    link: 'text-cyan-300' },
  amber:   { dot: 'bg-amber-400',   title: 'text-amber-300',   border: 'border-amber-500/40',   bg: 'bg-amber-900/20',   link: 'text-amber-300' },
  red:     { dot: 'bg-red-400',     title: 'text-red-300',     border: 'border-red-500/40',     bg: 'bg-red-900/20',     link: 'text-red-300' },
  pink:    { dot: 'bg-pink-400',    title: 'text-pink-300',    border: 'border-pink-500/40',    bg: 'bg-pink-900/20',    link: 'text-pink-300' },
  purple:  { dot: 'bg-purple-400',  title: 'text-purple-300',  border: 'border-purple-500/40',  bg: 'bg-purple-900/20',  link: 'text-purple-300' },
  violet:  { dot: 'bg-violet-400',  title: 'text-violet-300',  border: 'border-violet-500/40',  bg: 'bg-violet-900/20',  link: 'text-violet-300' },
  blue:    { dot: 'bg-blue-400',    title: 'text-blue-300',    border: 'border-blue-500/40',    bg: 'bg-blue-900/20',    link: 'text-blue-300' },
  teal:    { dot: 'bg-teal-400',    title: 'text-teal-300',    border: 'border-teal-500/40',    bg: 'bg-teal-900/20',    link: 'text-teal-300' },
  slate:   { dot: 'bg-slate-400',   title: 'text-slate-300',   border: 'border-slate-600/50',   bg: 'bg-slate-900/40',   link: 'text-blue-300' },
};

const linkify = (text, linkClass) => {
  const parts = text.split(URL_RE);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    URL_RE.test(part) ? (
      <a
        key={i}
        href={safeHref(part)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="source-reference-link"
        className={`inline-flex items-baseline gap-1 ${linkClass} underline underline-offset-2 break-all py-0.5`}
      >
        {part.length > 60 ? part.substring(0, 60) + '…' : part}
        <ExternalLink className="w-3 h-3 inline-block shrink-0 translate-y-0.5" />
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

// Rend un texte IA brut (puces •, tirets -, listes 1., liens) en liste lisible sur mobile
export const RichText = ({ text, accent = 'indigo', className = '' }) => {
  if (!text) return null;
  const a = ACCENTS[accent] || ACCENTS.indigo;
  const lines = text.split('\n');
  const hasBullets = lines.some((l) => /^\s*[•●▪■]/.test(l));
  return (
    <div className={`text-[15px] sm:text-sm leading-[1.65] sm:leading-relaxed text-slate-100 space-y-1 break-words ${className}`}>
      {lines.map((raw, i) => {
        const line = raw.replace(/\s+$/, '');
        const t = line.trim();
        if (!t) return <div key={i} className="h-1.5" />;
        let m;
        if ((m = t.match(/^[•●▪■]\s*(.*)$/)) || (!hasBullets && (m = t.match(/^[-–—]\s+(.*)$/)))) {
          return (
            <div key={i} className="flex gap-2.5">
              <span className={`mt-[9px] w-1.5 h-1.5 rounded-full shrink-0 ${a.dot}`} />
              <span className="flex-1 min-w-0">{linkify(m[1], a.link)}</span>
            </div>
          );
        }
        if ((m = t.match(/^[-–—]\s+(.*)$/))) {
          return (
            <div key={i} className="flex gap-2 pl-4 sm:pl-5 text-slate-200">
              <span className="text-slate-500 shrink-0 select-none">–</span>
              <span className="flex-1 min-w-0">{linkify(m[1], a.link)}</span>
            </div>
          );
        }
        if ((m = t.match(/^(\d{1,2})[.)]\s+(.*)$/))) {
          return (
            <div key={i} className="flex gap-2.5">
              <span className={`mt-0.5 w-6 h-6 rounded-md text-xs font-bold flex items-center justify-center shrink-0 ${a.bg} ${a.title} border ${a.border}`}>{m[1]}</span>
              <span className="flex-1 min-w-0 pt-0.5">{linkify(m[2], a.link)}</span>
            </div>
          );
        }
        if (t.length < 70 && /:$/.test(t)) {
          return <div key={i} className="font-semibold text-white pt-1">{t}</div>;
        }
        return <div key={i}>{linkify(t, a.link)}</div>;
      })}
    </div>
  );
};

// Carte de section compacte (bordure fine + titre court) — mobile-first
export const ResultCard = ({ title, accent = 'indigo', children, testId = 'ai-section-card', id }) => {
  const a = ACCENTS[accent] || ACCENTS.indigo;
  return (
    <div id={id} data-testid={testId} className={`rounded-xl border ${a.border} ${a.bg} p-3 sm:p-4`}>
      {title && (
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/5">
          <span className={`w-1 h-4 rounded-full ${a.dot}`} />
          <h3 className={`text-[13px] sm:text-sm font-semibold tracking-wide ${a.title}`}>{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
};

const prettyTitle = (raw) => {
  const t = raw.trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
};

const accentFor = (title, fallback) => {
  const u = title.toUpperCase();
  if (/ATTENTION|ALERTE|URGENCE|CONTRE-INDIC|DANGER|INTERACTION|RISQUE/.test(u)) return 'amber';
  if (/SOURCE|R[ÉE]F[ÉE]RENCE|BIBLIO/.test(u)) return 'slate';
  if (/ABSENT|NON TROUV|AUCUN/.test(u)) return 'red';
  return fallback;
};

export const parseBracketSections = (text) => {
  const sections = [];
  let current = null;
  (text || '').split('\n').forEach((line) => {
    const m = line.match(/^\s*\[([^\]]{2,80})\]\s*(.*)$/);
    if (m) {
      if (current) sections.push(current);
      current = { title: m[1], lines: m[2] ? [m[2]] : [] };
    } else if (line.trim().startsWith('━')) {
      // séparateur décoratif ignoré
    } else if (current) {
      current.lines.push(line);
    } else if (line.trim()) {
      current = { title: null, lines: [line] };
    }
  });
  if (current) sections.push(current);
  return sections.map((s) => ({ ...s, content: s.lines.join('\n').trim() })).filter((s) => s.content || s.title);
};

// Rendu complet d'une réponse IA brute avec sections [TITRE]
export const AIResultSections = ({ text, accent = 'indigo', testId = 'ai-result-sections', rateFeature, rateTargetId }) => {
  if (!text) return null;
  const sections = parseBracketSections(text);
  return (
    <div data-testid={testId} className="space-y-2.5 sm:space-y-3">
      {sections.map((s, i) => {
        const acc = s.title ? accentFor(s.title, accent) : accent;
        return (
          <ResultCard key={i} title={s.title ? prettyTitle(s.title) : null} accent={acc}>
            <RichText text={s.content} accent={acc} />
          </ResultCard>
        );
      })}
      {rateFeature && <RatingWidget feature={rateFeature} targetId={rateTargetId} />}
    </div>
  );
};
